"use client"

import clsx from "clsx"
import React, { memo } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils/ComponentProps"
import { TIcon } from "../display/TIcon"
import { ToggleGroup, ToggleGroupItem } from "../shadcn/toggle-group"
import { TToggleGroupItem } from "./types"

/**
 * @description 토글 버튼 그룹. single/multiple 선택 모드 지원
 * @example
 * <TToggleGroup
 *   type="single"
 *   items={[{ value: "bold", label: "B" }, { value: "italic", label: "I" }]}
 *   value={["bold"]}
 *   onChange={setSelected}
 * />
 */
export interface TToggleGroupProps<V extends string | number> extends BaseInteractiveProps {
	/** 토글 항목 배열. { value, label, icon? } 형태 */
	items: Array<TToggleGroupItem<V>>
	/** 현재 선택된 값 배열 */
	value?: Array<V>
	/** 선택 값 변경 콜백. 항상 배열로 전달 */
	onChange: (value: Array<V>) => void
	/**
	 * 선택 모드
	 * @ai-style "single"=하나만 선택 가능(라디오), "multiple"=복수 선택 가능(체크박스)
	 * @default "single"
	 */
	type?: "single" | "multiple"
	/** 토글 그룹 색상 테마. "default"=기본, "line"=외곽선 스타일 */
	color?: "default" | "line"
	/**
	 * 버튼 크기
	 * @ai-style xsmall=24px높이, small=32px높이, medium=40px높이, large=48px높이
	 * @default "medium"
	 */
	size?: "xsmall" | "small" | "medium" | "large"
	/**
	 * 둥근 모서리 적용
	 * @ai-style true=완전한 원형(pill) 모서리
	 * @default false
	 */
	rounded?: boolean
}

function TToggleGroupInner<V extends string | number>(props: TToggleGroupProps<V>) {
	const { id, items, value = [], type = "single", color = "default", size = "medium", disabled = false, rounded = false, className, dataAttributes, onChange } = props

	const dataProps = useDataAttributes(dataAttributes)
	const commonProps = React.useMemo(
		() => ({
			id,
			color,
			size,
			disabled
		}),
		[id, color, size, disabled]
	)

	const renderItems = React.useMemo(
		() =>
			items.map((item, idx) => {
				const itemDisabled = disabled || item.disabled
				return (
					<ToggleGroupItem key={idx} value={String(item.value)} disabled={itemDisabled} color={color}>
						{item.icon && <TIcon name={item.icon} />}
						{item.label && <span>{item.label}</span>}
					</ToggleGroupItem>
				)
			}),
		[items, disabled]
	)

	const handleSingleValueChange = React.useCallback(
		(value: string) => {
			if (!value) {
				return
			}
			onChange([value as V])
		},
		[onChange]
	)

	const handleMultipleValueChange = React.useCallback(
		(values: string[]) => {
			onChange(values as V[])
		},
		[onChange]
	)

	if (type === "single") {
		const singleValue = value[0] as string
		return (
			<ToggleGroup
				type="single"
				value={singleValue}
				{...commonProps}
				{...dataProps}
				className={clsx({ "rounded-full *:rounded-full": rounded }, className)}
				onValueChange={handleSingleValueChange}
			>
				{renderItems}
			</ToggleGroup>
		)
	} else {
		const multipleValues = type === "multiple" ? (value as V[]).map(String) : []
		return (
			<ToggleGroup
				type="multiple"
				value={multipleValues}
				{...commonProps}
				{...dataProps}
				className={clsx({ "rounded-full *:rounded-full": rounded }, className)}
				onValueChange={handleMultipleValueChange}
			>
				{renderItems}
			</ToggleGroup>
		)
	}
}

TToggleGroupInner.displayName = "TToggleGroup"

export const TToggleGroup = memo(TToggleGroupInner) as typeof TToggleGroupInner & { displayName?: string }
